import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Users, 
  TrendingUp, 
  Calendar,
  Building2,
  RefreshCw,
  ArrowUp,
  ArrowDown,
  Minus,
  GraduationCap,
  BookOpen,
  Award,
  Target,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Filter
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie,
  Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Colores por tipo de producto
const TIPO_COLORS = {
  curso: '#3b82f6',      // blue
  certificado: '#10b981', // green
  diplomatura: '#8b5cf6'  // purple
};

// Colores para instancias
const INSTANCE_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16'
];

export default function Enrolados360Dashboard({ token }) {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [globalData, setGlobalData] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState('global');
  const [partnerData, setPartnerData] = useState(null);
  const [evolucionData, setEvolucionData] = useState(null);
  const [error, setError] = useState(null);
  
  // F10: Estado para filtro de canal de venta
  const [canalesDisponibles, setCanalesDisponibles] = useState([]);
  const [selectedCanal, setSelectedCanal] = useState('todos');
  const [canalesConteo, setCanalesConteo] = useState({});
  const [tieneSinCanal, setTieneSinCanal] = useState(false);

  // F10: Fetch canales de venta disponibles
  const fetchCanales = useCallback(async (partnerSlug) => {
    // Solo para partners que usan HubSpot Deals
    if (!partnerSlug || partnerSlug === 'global' || !['siglo-21', 'areandina'].includes(partnerSlug)) {
      setCanalesDisponibles([]);
      setSelectedCanal('todos');
      setCanalesConteo({});
      setTieneSinCanal(false);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/enrolados360/canales-venta?partner_slug=${partnerSlug}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCanalesDisponibles(data.canales || []);
        setCanalesConteo(data.conteo || {});
        setTieneSinCanal(data.tiene_sin_canal || false);
      }
    } catch (err) {
      console.error('Error cargando canales:', err);
    }
  }, [token]);

  // Fetch global data
  const fetchGlobalData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/enrolados360/global`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Error cargando datos globales');
      const data = await response.json();
      setGlobalData(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch partner-specific data
  const fetchPartnerData = useCallback(async (slug) => {
    if (slug === 'global') {
      setPartnerData(null);
      return;
    }
    try {
      const [niResponse, evoResponse] = await Promise.all([
        fetch(`${API_URL}/api/enrolados360/ni/${slug}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/enrolados360/evolucion/${slug}?meses=12`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (!niResponse.ok) throw new Error('Error cargando datos del partner');
      
      const niData = await niResponse.json();
      const evoData = evoResponse.ok ? await evoResponse.json() : null;
      
      setPartnerData(niData);
      setEvolucionData(evoData);
    } catch (err) {
      setError(err.message);
    }
  }, [token]);

  useEffect(() => {
    fetchGlobalData();
  }, [fetchGlobalData]);

  useEffect(() => {
    fetchPartnerData(selectedPartner);
    fetchCanales(selectedPartner);  // F10: Cargar canales al cambiar partner
    setSelectedCanal('todos');       // F10: Reset filtro de canal
  }, [selectedPartner, fetchPartnerData, fetchCanales]);

  // Bug backlog: al reiniciar el backend, el resync MongoDB->SQLite (~55s)
  // deja el dashboard vacío. /api/ready (sin auth) devuelve 503 mientras
  // está sincronizando, así mostramos un banner claro en vez de tarjetas vacías.
  useEffect(() => {
    let cancelled = false;
    let timer;
    let wasSyncing = false;

    const poll = async () => {
      try {
        const response = await fetch(`${API_URL}/api/ready`);
        if (cancelled) return;
        if (response.status === 503) {
          wasSyncing = true;
          setSyncing(true);
          timer = setTimeout(poll, 5000);
        } else {
          setSyncing(false);
          if (wasSyncing) {
            // Los datos pudieron haber llegado vacíos durante el resync: refrescar.
            fetchGlobalData();
            fetchPartnerData(selectedPartner);
          }
        }
      } catch (err) {
        if (!cancelled) setSyncing(false);
      }
    };
    poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [fetchGlobalData, fetchPartnerData, selectedPartner]);

  // Prepare chart data for global evolution
  const prepareGlobalEvolution = () => {
    if (!globalData?.instancias) return [];
    
    // Aggregate ni_por_mes from all instances
    const allMonths = {};
    globalData.instancias.forEach(inst => {
      // This is a simplified view - in real impl we'd need per-instance monthly data
    });
    
    // For now, create mock evolution based on available data
    const months = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = d.toISOString().slice(0, 7);
      months.push({
        mes: monthKey,
        ni: Math.floor(Math.random() * 500) + 100 // Placeholder - would come from real data
      });
    }
    return months;
  };

  // Format fuente label
  const formatFuente = (fuente) => {
    const labels = {
      'canvas': 'Canvas',
      'archivo': 'Archivo (Slot 7)',
      'hubspot_deals': 'HubSpot Deals',
      'ccf_enrollment_date': 'CCF',
      'no_configurada': 'No configurada'
    };
    return labels[fuente] || fuente;
  };

  // Format currency
  const formatCurrency = (value) => {
    if (!value || value === 0) return null;
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0
    }).format(value);
  };

  // Componente de semáforo de cumplimiento
  const SemaforoIcon = ({ semaforo, size = 'w-5 h-5' }) => {
    if (semaforo === 'verde') {
      return <CheckCircle2 className={`${size} text-green-500`} />;
    } else if (semaforo === 'amarillo') {
      return <AlertTriangle className={`${size} text-yellow-500`} />;
    } else {
      return <XCircle className={`${size} text-red-500`} />;
    }
  };

  // Componente de tarjeta de Forecast
  const ForecastCard = ({ forecast, title = "Forecast 2026" }) => {
    if (!forecast) return null;
    
    const semaforoColors = {
      verde: 'border-green-200 bg-green-50',
      amarillo: 'border-yellow-200 bg-yellow-50',
      rojo: 'border-red-200 bg-red-50'
    };
    
    return (
      <Card className={`${semaforoColors[forecast.semaforo] || 'border-gray-200'}`}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Target className="w-5 h-5" />
            {title}
            <SemaforoIcon semaforo={forecast.semaforo} />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            {/* NI Real YTD */}
            <div>
              <p className="text-gray-500">NI Real YTD</p>
              <p className="text-xl font-bold text-blue-600">
                {forecast.ni_real_ytd?.toLocaleString() || 0}
              </p>
              <p className="text-xs text-gray-400">{forecast.months_closed} meses cerrados</p>
            </div>
            
            {/* Forecast YTD */}
            <div>
              <p className="text-gray-500">Forecast YTD</p>
              <p className="text-xl font-bold text-gray-700">
                {forecast.forecast_ytd?.toLocaleString() || 0}
              </p>
            </div>
            
            {/* % Cumplimiento */}
            <div>
              <p className="text-gray-500">% Cumplimiento</p>
              <p className={`text-xl font-bold ${
                forecast.semaforo === 'verde' ? 'text-green-600' :
                forecast.semaforo === 'amarillo' ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {forecast.cumplimiento_pct?.toFixed(1)}%
              </p>
            </div>
            
            {/* Forecast Anual */}
            <div>
              <p className="text-gray-500">Forecast Anual</p>
              <p className="text-xl font-bold text-gray-700">
                {forecast.forecast_annual?.toLocaleString() || 0}
              </p>
            </div>
            
            {/* Proyección Anual */}
            <div>
              <p className="text-gray-500">Proyección Anual</p>
              <p className="text-xl font-bold text-purple-600">
                {forecast.proyeccion_annual?.toLocaleString() || 0}
              </p>
              <p className="text-xs text-gray-400">Real + forecast restante</p>
            </div>
            
            {/* Diferencia */}
            <div>
              <p className="text-gray-500">Diferencia</p>
              <p className={`text-xl font-bold ${forecast.diferencia >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {forecast.diferencia >= 0 ? '+' : ''}{forecast.diferencia?.toLocaleString() || 0}
              </p>
              <p className="text-xs text-gray-400">vs forecast anual</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Render KPI card
  const KPICard = ({ title, value, subtitle, icon: Icon, color = 'blue', trend = null }) => (
    <Card className="relative overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className={`text-3xl font-bold text-${color}-600`}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </p>
            {subtitle && (
              <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
            )}
          </div>
          <div className={`p-3 bg-${color}-100 rounded-full`}>
            <Icon className={`w-6 h-6 text-${color}-600`} />
          </div>
        </div>
        {trend !== null && (
          <div className="mt-2 flex items-center text-sm">
            {trend > 0 ? (
              <ArrowUp className="w-4 h-4 text-green-500 mr-1" />
            ) : trend < 0 ? (
              <ArrowDown className="w-4 h-4 text-red-500 mr-1" />
            ) : (
              <Minus className="w-4 h-4 text-gray-400 mr-1" />
            )}
            <span className={trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-500'}>
              {Math.abs(trend)}% vs mes anterior
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (syncing) {
    return (
      <div
        className="flex flex-col items-center justify-center h-64 gap-3"
        data-testid="enrolados-syncing-banner"
      >
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        <div className="text-center">
          <p className="text-gray-700 font-medium">Sincronizando datos...</p>
          <p className="text-gray-400 text-sm mt-1">
            El servidor se está actualizando, esto puede tardar hasta 1 minuto.
          </p>
        </div>
      </div>
    );
  }

  if (loading && !globalData) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Cargando Enrolados 360...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="enrolados360-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Enrolados 360</h1>
          <p className="text-gray-500">
            Nuevos Ingresos (NI) por instancia • 
            <span className="text-xs ml-1 text-gray-400">
              NI = alumnos enrolados, ubicados en el mes de inicio de su cohorte (no incluye becas/bonificados)
            </span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedPartner} onValueChange={setSelectedPartner}>
            <SelectTrigger className="w-48" data-testid="partner-selector">
              <SelectValue placeholder="Seleccionar instancia" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="global">Vista Global</SelectItem>
              {globalData?.instancias?.map(inst => (
                <SelectItem key={inst.partner_slug} value={inst.partner_slug}>
                  {inst.partner_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchGlobalData}
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Actualizar
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Global View */}
      {selectedPartner === 'global' && globalData && (
        <>
          {/* NÚMERO PRINCIPAL: NI Acumulado 2026 */}
          <Card className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium">NI Acumulado 2026</p>
                  <p className="text-5xl font-bold mt-1" data-testid="ni-2026-principal">
                    {(globalData.total_ni_2026 || 0).toLocaleString()}
                  </p>
                  <p className="text-blue-200 text-xs mt-2">
                    Nuevos Ingresos al inicio de cohorte (académico)
                  </p>
                  {globalData.ccf_excluido && (
                    <p className="text-amber-200 text-xs mt-1 flex items-center gap-1">
                      <span>⚠️</span>
                      <span>Excluye CCF ({globalData.ccf_historico?.toLocaleString() || '—'} histórico) — fecha sintética</span>
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <div className="bg-white/10 rounded-lg p-4">
                    <p className="text-blue-100 text-xs">Facturación Estimada USD</p>
                    <p className="text-2xl font-bold">
                      ${(globalData.total_facturacion_estimada_usd || 0).toLocaleString()}
                    </p>
                    <p className="text-blue-200 text-xs mt-1">NI × Ticket Promedio</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* KPIs secundarios */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KPICard
              title="NI Hoy"
              value={globalData.has_any_baseline ? globalData.total_hoy : '—'}
              subtitle={globalData.has_any_baseline 
                ? "Nuevos ingresos del día" 
                : "Baseline creándose"
              }
              icon={Calendar}
              color="purple"
            />
            <KPICard
              title="MTD (Mes actual)"
              value={globalData.total_mtd}
              subtitle="Acumulado del mes"
              icon={Users}
              color="orange"
            />
            <KPICard
              title="Instancias Activas"
              value={globalData.instancias?.length || 0}
              subtitle={`De ${globalData.instancias_configuradas?.length || 0} configuradas`}
              icon={Building2}
              color="blue"
            />
          </div>

          {/* Forecast 2026 - Global */}
          {globalData.forecast && (
            <div className="space-y-2">
              <ForecastCard forecast={globalData.forecast} title="Forecast 2026" />
            </div>
          )}

          {/* Instances Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                NI por Instancia
              </CardTitle>
              <CardDescription>
                Desglose de Nuevos Ingresos por partner
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Instancia</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Fuente</th>
                      <th className="text-right py-3 px-4 font-medium text-blue-700" title="Nuevos Ingresos 2026 (académico)">
                        NI 2026
                      </th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">% Cump.</th>
                      <th className="text-right py-3 px-4 font-medium text-purple-700" title="NI × Ticket Promedio USD">
                        Fact. Est. USD
                      </th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">NI Hoy</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">Forecast</th>
                    </tr>
                  </thead>
                  <tbody>
                    {globalData.instancias?.map((inst, idx) => (
                      <tr 
                        key={inst.partner_slug} 
                        className={`border-b hover:bg-gray-50 ${inst.is_placeholder ? 'bg-gray-50/50 italic' : ''} ${inst.fecha_sintetica ? 'bg-amber-50/50' : ''} ${!inst.is_placeholder ? 'cursor-pointer' : ''}`}
                        onClick={() => !inst.is_placeholder && setSelectedPartner(inst.partner_slug)}
                        data-testid={`instance-row-${inst.partner_slug}`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div 
                              className={`w-3 h-3 rounded-full ${inst.is_placeholder ? 'bg-gray-300' : ''}`}
                              style={{ backgroundColor: inst.is_placeholder ? undefined : INSTANCE_COLORS[idx % INSTANCE_COLORS.length] }}
                            />
                            <span className={`font-medium ${inst.is_placeholder ? 'text-gray-500' : ''}`}>{inst.partner_name}</span>
                            {inst.fecha_sintetica && (
                              <span className="text-xs text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded" title={inst.ni_2026_nota}>
                                ⚠️ 2026 N/D
                              </span>
                            )}
                            {inst.is_placeholder && (
                              <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                                pendiente
                              </span>
                            )}
                          </div>
                          {inst.fecha_sintetica && (
                            <p className="text-xs text-amber-600 mt-0.5 pl-5">
                              Histórico: {inst.ni_total_historico?.toLocaleString()} — fecha sintética
                            </p>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className={`text-xs ${inst.is_placeholder ? 'text-gray-400' : ''}`}>
                            {formatFuente(inst.fuente)}
                          </Badge>
                        </td>
                        {/* NI 2026 (Enrolados - académico) */}
                        <td className="py-3 px-4 text-right font-mono font-medium text-blue-600">
                          {inst.is_placeholder ? (
                            <span className="text-gray-400">0</span>
                          ) : inst.fecha_sintetica ? (
                            <span className="text-gray-400" title={inst.ni_2026_nota}>
                              N/A
                              <span className="block text-xs text-amber-500">⚠️ fecha sintética</span>
                            </span>
                          ) : (
                            (inst.ni_2026 || 0).toLocaleString()
                          )}
                        </td>
                        {/* % Cumplimiento */}
                        <td className="py-3 px-4 text-right">
                          {inst.is_placeholder ? (
                            <span className="text-gray-400">—</span>
                          ) : inst.fecha_sintetica ? (
                            <span className="text-gray-300">—</span>
                          ) : inst.forecast ? (
                            <div className="flex items-center justify-end gap-1">
                              <SemaforoIcon semaforo={inst.forecast.semaforo} size="w-4 h-4" />
                              <span className={`font-medium ${
                                inst.forecast.semaforo === 'verde' ? 'text-green-600' :
                                inst.forecast.semaforo === 'amarillo' ? 'text-yellow-600' : 'text-red-600'
                              }`}>
                                {inst.forecast.cumplimiento_pct?.toFixed(0)}%
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        {/* Facturación Estimada USD */}
                        <td className="py-3 px-4 text-right font-mono text-purple-600">
                          {inst.is_placeholder ? (
                            <span className="text-gray-400">—</span>
                          ) : inst.fecha_sintetica ? (
                            <span className="text-gray-400">N/A</span>
                          ) : (
                            <>
                              ${(inst.facturacion_estimada_usd || 0).toLocaleString()}
                              <span className="text-xs text-gray-400 block">@${inst.ticket_promedio_usd}</span>
                            </>
                          )}
                        </td>
                        {/* NI Hoy */}
                        <td className="py-3 px-4 text-right font-mono">
                          {inst.is_placeholder ? (
                            <span className="text-gray-400">—</span>
                          ) : inst.fecha_sintetica ? (
                            <span className="text-gray-400">—</span>
                          ) : inst.has_baseline === false ? (
                            <span className="text-gray-400" title="Sin baseline - se mostrará mañana">—</span>
                          ) : inst.nuevos_hoy > 0 ? (
                            <span className="text-green-600 font-medium">+{inst.nuevos_hoy}</span>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </td>
                        {/* Forecast Anual */}
                        <td className="py-3 px-4 text-right font-mono">
                          {inst.is_placeholder ? (
                            <span className="text-gray-500">{inst.forecast?.forecast_annual?.toLocaleString() || '—'}</span>
                          ) : inst.fecha_sintetica ? (
                            <span className="text-gray-400">—</span>
                          ) : (
                            inst.forecast?.forecast_annual?.toLocaleString() || '—'
                          )}
                        </td>
                      </tr>
                    ))}
                    {/* Fila de totales */}
                    <tr className="bg-gray-100 font-bold">
                      <td className="py-3 px-4" colSpan={2}>TOTAL</td>
                      <td className="py-3 px-4 text-right font-mono text-blue-700">
                        {(globalData.total_ni_2026 || 0).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {globalData.forecast ? (
                          <span className={
                            globalData.forecast.semaforo === 'verde' ? 'text-green-600' :
                            globalData.forecast.semaforo === 'amarillo' ? 'text-yellow-600' : 'text-red-600'
                          }>
                            {globalData.forecast.cumplimiento_pct?.toFixed(0)}%
                          </span>
                        ) : '—'}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-purple-700">
                        ${(globalData.total_facturacion_estimada_usd || 0).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right font-mono">
                        {globalData.has_any_baseline ? globalData.total_hoy : '—'}
                      </td>
                      <td className="py-3 px-4 text-right font-mono">
                        {globalData.forecast_empresa_total?.toLocaleString() || '—'}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <p className="text-xs text-gray-400 mt-2 px-4">
                  💡 NI = Alumnos enrolados por mes de inicio de cohorte • Fact. Est. = NI × Ticket USD (estimación)
                </p>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Partner Detail View */}
      {selectedPartner !== 'global' && partnerData && (
        <>
          {/* Partner Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setSelectedPartner('global')}
              >
                ← Volver a Global
              </Button>
              <Badge variant="outline">
                {formatFuente(partnerData.fuente)}
              </Badge>
            </div>
            
            {/* F10: Filtro de Canal de Venta - Solo para HubSpot Deals */}
            {partnerData.fuente === 'hubspot_deals' && canalesDisponibles.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Canal de Venta:</span>
                <Select 
                  value={selectedCanal} 
                  onValueChange={setSelectedCanal}
                  data-testid="canal-venta-selector"
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Todos los canales" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">
                      Todos ({Object.values(canalesConteo).reduce((a, b) => a + b, 0)})
                    </SelectItem>
                    {canalesDisponibles.map(canal => (
                      <SelectItem key={canal} value={canal}>
                        {canal} ({canalesConteo[canal] || 0})
                      </SelectItem>
                    ))}
                    {tieneSinCanal && (
                      <SelectItem value="_sin_canal">
                        Sin canal ({canalesConteo['_sin_canal'] || 0})
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KPICard
              title="NI Hoy (Delta)"
              value={partnerData.delta_hoy || 0}
              subtitle="Nuevos vs ayer"
              icon={TrendingUp}
              color="green"
            />
            <KPICard
              title="MTD"
              value={partnerData.proyeccion?.mtd || 0}
              subtitle="Acumulado mes actual"
              icon={Calendar}
              color="purple"
            />
            <KPICard
              title="Proyección"
              value={partnerData.proyeccion?.proyeccion || 0}
              subtitle={`Método: ${partnerData.proyeccion?.metodo || 'N/A'}`}
              icon={TrendingUp}
              color="orange"
            />
          </div>
          
          {/* Forecast de la instancia */}
          {partnerData.forecast && (
            <ForecastCard 
              forecast={partnerData.forecast} 
              title={`Forecast 2026 - ${partnerData.partner_name}`} 
            />
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* NI por Mes */}
            <Card>
              <CardHeader>
                <CardTitle>Evolución Mensual</CardTitle>
                <CardDescription>Alumnos enrolados según el mes en que inicia su cohorte (meses futuros = cohortes aún no iniciadas)</CardDescription>
              </CardHeader>
              <CardContent>
                {Object.keys(partnerData.ni_por_mes || {}).length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={Object.entries(partnerData.ni_por_mes).map(([mes, ni]) => ({ mes, ni }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="mes" fontSize={12} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="ni" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center text-gray-400">
                    Sin datos de evolución mensual
                  </div>
                )}
              </CardContent>
            </Card>

            {/* NI por Programa (canvas, o distribución estimada para Areandina) */}
            <Card data-testid="ni-por-programa-card">
              <CardHeader>
                <CardTitle>{partnerData.ni_por_programa_label || 'NI por Programa'}</CardTitle>
                <CardDescription>
                  {partnerData.ni_por_programa_fuente === 'canvas_normalizadora' && partnerData.ni_por_programa_coincide_con_total === false
                    ? 'Distribución de alumnos de Canvas agrupados por programa (no es el NI oficial)'
                    : 'Top programas por cantidad de NI'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Aviso de reconciliación: el desglose por programa (Canvas) puede no sumar
                    exacto al NI oficial (archivo/Slot 7) - nunca mostrar sin esta aclaración */}
                {partnerData.ni_por_programa_fuente === 'canvas_normalizadora' && 
                 partnerData.ni_por_programa_coincide_con_total === false && (
                  <div 
                    className="flex items-start gap-2 mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800"
                    data-testid="ni-por-programa-reconciliacion-warning"
                  >
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                      Distribución estimada a partir de Canvas ({partnerData.ni_por_programa_suma} alumnos) - 
                      no necesariamente suma igual al NI oficial del archivo ({partnerData.ni_total}).
                    </span>
                  </div>
                )}
                {/* Usar nombres normalizados si están disponibles, sino códigos */}
                {(() => {
                  const normalizado = partnerData.ni_por_programa_normalizado?.por_nombre || {};
                  const porCodigo = partnerData.ni_por_programa || {};
                  const dataToShow = Object.keys(normalizado).length > 0 ? normalizado : porCodigo;
                  const hasData = Object.keys(dataToShow).length > 0;
                  
                  return hasData ? (
                    <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="ni-por-programa-list">
                      {Object.entries(dataToShow)
                        .slice(0, 15)
                        .map(([programa, count], idx) => (
                          <div key={programa} className="flex items-center justify-between py-2 border-b" data-testid={`ni-por-programa-row-${idx}`}>
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className="text-sm font-medium text-gray-500 w-6 flex-shrink-0">{idx + 1}.</span>
                              <span className="text-sm truncate" title={programa}>{programa}</span>
                            </div>
                            <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-sm font-medium flex-shrink-0 ml-2">
                              {count}
                            </span>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-gray-400">
                      {partnerData.fuente === 'archivo' 
                        ? 'Desglose por programa no disponible para archivo'
                        : 'Sin datos de programas'
                      }
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* NI por Tipo */}
            <Card>
              <CardHeader>
                <CardTitle>NI por Tipo de Producto</CardTitle>
                <CardDescription>Curso, Certificado, Diplomatura</CardDescription>
              </CardHeader>
              <CardContent>
                {Object.keys(partnerData.ni_por_tipo || {}).length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={Object.entries(partnerData.ni_por_tipo).map(([tipo, count]) => ({
                          name: tipo.charAt(0).toUpperCase() + tipo.slice(1),
                          value: count,
                          fill: TIPO_COLORS[tipo] || '#94a3b8'
                        }))}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {Object.entries(partnerData.ni_por_tipo).map(([tipo], index) => (
                          <Cell key={`cell-${index}`} fill={TIPO_COLORS[tipo] || '#94a3b8'} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center text-gray-400">
                    Sin datos de tipo de producto
                  </div>
                )}
              </CardContent>
            </Card>

            {/* F10: NI por Canal de Venta - Solo para HubSpot Deals */}
            {partnerData.fuente === 'hubspot_deals' && Object.keys(canalesConteo).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Filter className="w-5 h-5" />
                    NI por Canal de Venta
                  </CardTitle>
                  <CardDescription>
                    Desglose por origen comercial (HubSpot)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(canalesConteo)
                      .filter(([canal]) => canal !== '_sin_canal')
                      .map(([canal, count]) => {
                        const total = Object.values(canalesConteo).reduce((a, b) => a + b, 0);
                        const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                        return (
                          <div 
                            key={canal} 
                            className={`flex items-center justify-between py-2 px-3 rounded-lg cursor-pointer transition-colors ${
                              selectedCanal === canal 
                                ? 'bg-blue-100 border border-blue-300' 
                                : 'bg-gray-50 hover:bg-gray-100'
                            }`}
                            onClick={() => setSelectedCanal(selectedCanal === canal ? 'todos' : canal)}
                          >
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-blue-500" />
                              <span className="font-medium">{canal}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-gray-500">{percentage}%</span>
                              <Badge variant="secondary">{count.toLocaleString()}</Badge>
                            </div>
                          </div>
                        );
                      })}
                    {tieneSinCanal && canalesConteo['_sin_canal'] > 0 && (
                      <div 
                        className={`flex items-center justify-between py-2 px-3 rounded-lg cursor-pointer transition-colors ${
                          selectedCanal === '_sin_canal' 
                            ? 'bg-gray-200 border border-gray-400' 
                            : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                        onClick={() => setSelectedCanal(selectedCanal === '_sin_canal' ? 'todos' : '_sin_canal')}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-gray-400" />
                          <span className="font-medium text-gray-600">Sin canal asignado</span>
                        </div>
                        <Badge variant="outline">{canalesConteo['_sin_canal'].toLocaleString()}</Badge>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-3">
                    💡 Click en un canal para filtrar la vista
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Leyenda de Tipos */}
            <Card>
              <CardHeader>
                <CardTitle>Tipos de Producto</CardTitle>
                <CardDescription>Definición de cada tipo</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <BookOpen className="w-5 h-5 text-blue-500 mt-1" />
                    <div>
                      <p className="font-medium">Curso</p>
                      <p className="text-sm text-gray-500">1 curso, 1 mes de duración</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Award className="w-5 h-5 text-green-500 mt-1" />
                    <div>
                      <p className="font-medium">Certificado</p>
                      <p className="text-sm text-gray-500">4 cursos, 4 meses de duración</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <GraduationCap className="w-5 h-5 text-purple-500 mt-1" />
                    <div>
                      <p className="font-medium">Diplomatura</p>
                      <p className="text-sm text-gray-500">8 cursos, 8 meses de duración</p>
                    </div>
                  </div>
                </div>
                <div className="mt-6 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>Nota:</strong> NI = primera inscripción por alumno/programa. 
                    No se cuenta cada curso ni re-enrolamiento mensual.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Footer info */}
      <div className="text-center text-xs text-gray-400 mt-8">
        <p>
          Instancias configuradas: {globalData?.instancias_configuradas?.join(', ') || 'Ninguna'} • 
          NI = alumnos enrolados por mes de inicio de cohorte — no representa fecha de venta ni facturación real
        </p>
      </div>
    </div>
  );
}
